import { z } from 'zod'
import axios from 'axios'

/** Get the value from map for location.host, if it's a map. Consider 'default' and get the first URL, if no default is specified */
const urlOrMap = z.preprocess((val, ctx) => {
  let result = val
  try {
    const object = JSON.parse(String(val))
    if (typeof object === 'object') {
      let firstUrl = val
      for (const m in object) {
        if (firstUrl === val) {
          firstUrl = object[m]
        }
        const v = z.string().url().safeParse(object[m])
        if (!v.success) {
          ctx.addIssue({
            code: 'custom',
            fatal: true,
            message: `The URL '${object[m]}' for host '${m}' in Map is not a valid url`,
          })
          return z.NEVER
        }
      }

      if (z.string().url().safeParse(object[location?.host]).success) {
        result = object[location?.host]
      } else if (z.string().url().safeParse(object['default']).success) {
        result = object['default']
      } else {
        result = firstUrl
      }
      const verification = z.string().url().safeParse(result)
      if (!verification.success) {
        ctx.addIssue({
          code: 'custom',
          fatal: true,
          message: `No valid URL can be found in map '${result}'`,
        })
        return z.NEVER
      }
    } else if (typeof object === 'string') {
      result = object
    }
  } catch (e) {
    const verification = z.string().url().safeParse(result)
    if (!verification.success) {
      ctx.addIssue({
        code: 'custom',
        fatal: true,
        message: `The value '${result}' is not a valid URL and can not be parsed: ${
          (e as any).message
        }`,
      })
      return z.NEVER
    }
    // fall back to value
  }

  return result
}, z.string().url())

const envVariablesValidators = {
  ANALYZE_AWS_BUCKET: z.string(),
  ANALYZE_API_BACKEND_URL: urlOrMap,
  ANALYZE_OAUTH2_CLIENT_ID: z.string(),
  ANALYZE_OAUTH2_AUTHORIZE_URL: z.string().url(),
  ANALYZE_OAUTH2_TOKEN_URL: z.string().url(),
  ANALYZE_OAUTH2_SCOPES: z.string(),
  ANALYZE_OAUTH2_LOGOUT_URL: z.string().url(),
  ANALYZE_Response_Type: z.string().default('code'), // or 'token', note that REFRESH token will only be generated when the responseType is code
  ANALYZE_PLATFORM_URL: urlOrMap,
  ANALYZE_AGENT_RUNTIME_SERVICE_URL: z.string().url().optional(),
  ANALYZE_CHAT_BOT_MCP_URL: z.string().url().optional(),
}

export async function fetchEnvVars() {
  const { data } = await axios.get('/configuration.json')
  return extractEnvVars(data)
}

export const extractEnvVars = (from: Record<string, string | undefined>) => {
  return envKeys.reduce((envVars, envKey) => {
    envVars[envKey] = from[envKey]
    return envVars
  }, {} as UnparsedEnvVariables)
}

export const validateEnvVars = (from?: UnparsedEnvVariables | null): EnvValidationResult[] => {
  return envKeys.map((envKey) => {
    const validator = envVariablesValidators[envKey]
    const envValue = from?.[envKey]

    const result = validator.safeParse(envValue)

    if (result.success === false) {
      return {
        envKey,
        envValue,
        validationStatus: validator.isOptional() ? 'warning' : 'error',
        error:
          `Provided configuration value "${envValue ?? ''}" is not valid. Reason(s): ` +
          result.error.issues.map(({ message }) => message).join(', '),
      }
    }

    return { envKey, envValue: result.data, validationStatus: 'valid' }
  })
}

type EnvValidationResultValid = {
  envKey: EnvVariableKey
  envValue: EnvVariables[EnvVariableKey]
  validationStatus: 'valid'
}

type EnvValidationResultInvalid = {
  envKey: EnvVariableKey
  envValue: unknown
  validationStatus: 'warning' | 'error'
  error: string
}

export type EnvValidationResult = EnvValidationResultInvalid | EnvValidationResultValid

export function isValid(result: EnvValidationResult): result is EnvValidationResultValid {
  return result.validationStatus === 'valid'
}

const envValidator = z.object(envVariablesValidators)
export type EnvVariables = z.infer<typeof envValidator>
export type EnvVariableKey = keyof EnvVariables
export type UnparsedEnvVariables = Record<EnvVariableKey, unknown>
const envKeys = envValidator.keyof().options as EnvVariableKey[]
